import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';
import { useSession } from './api/queries';
import { Layout } from './components/Layout';
import { Spinner } from './components/Spinner';
import { AccountPage } from './pages/AccountPage';
import { AdminUserscriptPage } from './pages/AdminUserscriptPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AdminWorldsPage } from './pages/AdminWorldsPage';
import { AgPlanerPage } from './pages/AgPlanerPage';
import { AgRequestPage } from './pages/AgRequestPage';
import { AttackPackageTemplatesPage } from './pages/AttackPackageTemplatesPage';
import { AttackWorkbenchGroupingPage } from './pages/AttackWorkbenchGroupingPage';
import { AttackPlanDetailPage } from './pages/AttackPlanDetailPage';
import { AttackPlansPage } from './pages/AttackPlansPage';
import { DashboardPage } from './pages/DashboardPage';
import { IncomingAttacksPage } from './pages/IncomingAttacksPage';
import { InsertPage } from './pages/InsertPage';
import { LoginPage } from './pages/LoginPage';
import { DiscordSettingsPage } from './pages/DiscordSettingsPage';
import { ManagementPage } from './pages/ManagementPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { RegisterPage } from './pages/RegisterPage';
import { ReportsPage } from './pages/ReportsPage';
import { RetimeRequestsPage } from './pages/RetimeRequestsPage';
import { SdRequestsPage } from './pages/SdRequestsPage';
import { SendWindowsPage } from './pages/SendWindowsPage';
import { VillageIntelPage } from './pages/VillageIntelPage';
import { isPlayerVerified, type User } from './api/types';

/** Nur fuer angemeldete Konten; merkt sich das Ziel fuer nach der Anmeldung. */
function RequireUser({ user, children }: { user: User | null; children: ReactElement }) {
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

function RequireAdmin({ user, children }: { user: User | null; children: ReactElement }) {
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') {
    return (
      <section className="card card-narrow center">
        <p className="error-code">403</p>
        <h1>Kein Zugriff</h1>
        <p className="muted">Dieser Bereich ist Administratoren vorbehalten.</p>
      </section>
    );
  }
  return children;
}

function RequireTribeLeaderOrAdmin({ user, children }: { user: User | null; children: ReactElement }) {
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin' && user.role !== 'tribeLeader') {
    return (
      <section className="card card-narrow center">
        <p className="error-code">403</p>
        <h1>Kein Zugriff</h1>
        <p className="muted">Dieser Bereich ist Stammesführern und Administratoren vorbehalten.</p>
      </section>
    );
  }
  return children;
}

/**
 * Sperrt eine Fläche für Konten ohne verifizierte Spielfigur -- das ist
 * standardmäßig fast das ganze Portal, dieselbe Regel wie serverseitig
 * (requireVerifiedPlayer, server/src/api/middleware/auth.ts). Administratoren
 * sind ausgenommen (isPlayerVerified). Für jede neue Fläche gilt: mit
 * diesem Wächter versehen, außer es wurde ausdrücklich anders entschieden.
 */
function RequireVerifiedPlayer({ user, children }: { user: User | null; children: ReactElement }) {
  if (!user) return <Navigate to="/login" replace />;
  if (!isPlayerVerified(user)) {
    return (
      <section className="card card-narrow center">
        <p className="error-code">403</p>
        <h1>Spielfigur nicht verifiziert</h1>
        <p className="muted">Bevor du das nutzen kannst, muss deine Spielfigur verifiziert sein.</p>
        <Link className="btn btn-primary" to="/konto">
          Jetzt verifizieren
        </Link>
      </section>
    );
  }
  return children;
}

function RequireGuest({ user, children }: { user: User | null; children: ReactElement }) {
  if (user) return <Navigate to="/" replace />;
  return children;
}

export function App() {
  const session = useSession();

  if (session.isPending) {
    return (
      <main className="container center">
        <Spinner label="Sitzung wird geprüft …" />
      </main>
    );
  }

  if (session.isError) {
    return (
      <main className="container">
        <section className="card card-narrow center">
          <h1>Keine Verbindung</h1>
          <p className="muted">
            Der Server ist nicht erreichbar. Läuft die API? Prüfe die Konsole des Servers.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => session.refetch()}>
            Erneut versuchen
          </button>
        </section>
      </main>
    );
  }

  const user = session.data?.user ?? null;

  return (
    <Routes>
      <Route element={<Layout user={user} />}>
        <Route
          index
          element={
            <RequireUser user={user}>
              <DashboardPage user={user!} />
            </RequireUser>
          }
        />
        <Route
          path="insert"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <InsertPage user={user!} />
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="berichte"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <ReportsPage user={user!} />
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        {/* Absichtlich OHNE RequireVerifiedPlayer -- hier läuft der Verifizierungs-Wizard selbst. */}
        <Route
          path="konto"
          element={
            <RequireUser user={user}>
              <AccountPage user={user!} />
            </RequireUser>
          }
        />
        <Route
          path="abschickzeiten"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <SendWindowsPage />
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="angriffe"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <IncomingAttacksPage user={user!} />
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="dorftypen"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <VillageIntelPage user={user!} />
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="sd-anfragen"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <SdRequestsPage user={user!} />
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="retime-anfragen"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <RetimeRequestsPage user={user!} />
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="ag-abfrage"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <AgRequestPage user={user!} />
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="verwaltung"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <RequireTribeLeaderOrAdmin user={user}>
                  <ManagementPage user={user!} />
                </RequireTribeLeaderOrAdmin>
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="discord-einstellungen"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <RequireTribeLeaderOrAdmin user={user}>
                  <DiscordSettingsPage user={user!} />
                </RequireTribeLeaderOrAdmin>
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="angriffsplaner"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <RequireTribeLeaderOrAdmin user={user}>
                  <AttackPlansPage user={user!} />
                </RequireTribeLeaderOrAdmin>
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="ag-planer"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <RequireTribeLeaderOrAdmin user={user}>
                  <AgPlanerPage user={user!} />
                </RequireTribeLeaderOrAdmin>
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="angriffsplaner/pakettypen"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <RequireTribeLeaderOrAdmin user={user}>
                  <AttackPackageTemplatesPage user={user!} />
                </RequireTribeLeaderOrAdmin>
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="angriffsplaner/workbench-gruppieren"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <RequireTribeLeaderOrAdmin user={user}>
                  <AttackWorkbenchGroupingPage user={user!} />
                </RequireTribeLeaderOrAdmin>
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="angriffsplaner/plaene/:id"
          element={
            <RequireUser user={user}>
              <RequireVerifiedPlayer user={user}>
                <RequireTribeLeaderOrAdmin user={user}>
                  <AttackPlanDetailPage user={user!} />
                </RequireTribeLeaderOrAdmin>
              </RequireVerifiedPlayer>
            </RequireUser>
          }
        />
        <Route
          path="admin/welten"
          element={
            <RequireAdmin user={user}>
              <AdminWorldsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/benutzer"
          element={
            <RequireAdmin user={user}>
              <AdminUsersPage currentUser={user!} />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/userskript"
          element={
            <RequireAdmin user={user}>
              <AdminUserscriptPage />
            </RequireAdmin>
          }
        />
        <Route
          path="login"
          element={
            <RequireGuest user={user}>
              <LoginPage />
            </RequireGuest>
          }
        />
        <Route
          path="register"
          element={
            <RequireGuest user={user}>
              <RegisterPage />
            </RequireGuest>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
